import { computed, ref, shallowRef } from 'vue';

const enabled = window.chatwootConfig?.guandaliniThemeEnabled === true;
const userAgent = navigator.userAgent;
const isIos =
  /iPhone|iPad|iPod/.test(userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isAndroid = /Android/.test(userAgent);
const isSafari =
  isIos &&
  /Safari/.test(userAgent) &&
  !/CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent);
const isChrome =
  isAndroid &&
  /Chrome\//.test(userAgent) &&
  !/EdgA|OPR|SamsungBrowser/.test(userAgent);
const standalone = window.matchMedia(
  '(display-mode: standalone), (display-mode: fullscreen)'
);
const installed = ref(navigator.standalone === true || standalone.matches);
const deferredPrompt = shallowRef(null);
const visible = ref(false);
const instructions = ref(false);
const pending = ref(false);
const installError = ref(false);
const canOfferInstall = computed(
  () => enabled && (isIos || isAndroid) && !installed.value
);
const canPrompt = computed(() => Boolean(deferredPrompt.value));

const closeInstall = () => {
  visible.value = false;
};

const openInstall = () => {
  if (!canOfferInstall.value) return;
  instructions.value = false;
  installError.value = false;
  visible.value = true;
};

const install = async () => {
  if (pending.value) return;
  const event = deferredPrompt.value;
  if (!event) {
    instructions.value = true;
    return;
  }
  pending.value = true;
  installError.value = false;
  deferredPrompt.value = null;
  try {
    await event.prompt();
    await event.userChoice;
    closeInstall();
  } catch {
    installError.value = true;
    instructions.value = true;
  } finally {
    pending.value = false;
  }
};

// These listeners belong to the authenticated dashboard's document lifetime.
if (enabled) {
  window.addEventListener('beforeinstallprompt', event => {
    if (!canOfferInstall.value) return;
    event.preventDefault();
    deferredPrompt.value = event;
  });
  window.addEventListener('appinstalled', () => {
    installed.value = true;
    deferredPrompt.value = null;
    closeInstall();
  });
  standalone.addEventListener('change', event => {
    installed.value = navigator.standalone === true || event.matches;
    if (installed.value) closeInstall();
  });
}

export const useMobileAppInstall = () => ({
  canOfferInstall,
  canPrompt,
  visible,
  instructions,
  pending,
  installError,
  isIos,
  isSafari,
  isChrome,
  openInstall,
  closeInstall,
  install,
});
